import React from 'react';
import { LogOut, Globe2, Home, Monitor, Smartphone, RotateCw, Shield } from 'lucide-react';
import { ADMIN_PALETTE } from './adminDesignSystem';
import { AdminNotificationsBell } from './AdminNotificationsBell';

export const AdminPanelHeader = ({
  isMobileView,
  isMainAdmin,
  activeTab,
  onMapsClick,
  toggleMobileView,
  handleLogout,
  onResetClick,
  onAppViewClick,
  onSelectTab,
}) => (
  <div style={{
    background: ADMIN_PALETTE.surface,
    padding: isMobileView ? '10px 12px' : '16px 22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: `1px solid ${ADMIN_PALETTE.border}`,
    borderRadius: isMobileView ? 16 : 20,
    marginBottom: isMobileView ? 12 : 10,
    gap: 8,
    boxShadow: isMobileView ? '0 1px 2px rgba(15,17,21,0.04)' : undefined,
  }}>
    {/* Left: title + reset */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <Shield size={18} color={ADMIN_PALETTE.brandRed} style={{ flexShrink: 0 }} />
      <span style={{
        fontFamily: "'Inria Serif', serif",
        fontSize: isMobileView ? 18 : 20,
        fontWeight: 700,
        color: ADMIN_PALETTE.ink,
        whiteSpace: 'nowrap',
        letterSpacing: -0.2,
      }}>
        Admin{isMobileView ? '' : ' Panel'}
      </span>
      {isMainAdmin && !isMobileView && (
        <button
          type="button"
          data-testid="admin-header-launch-reset-button"
          onClick={onResetClick}
          title="Reset Allure — requires every main admin to confirm via email"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 4,
            padding: '7px 11px', borderRadius: 9999,
            border: `1px solid ${ADMIN_PALETTE.brandRed}40`,
            background: '#FFE2E2', color: ADMIN_PALETTE.brandRed,
            cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: 12,
          }}
        >
          <RotateCw size={13} /> Reset
        </button>
      )}
    </div>

    {/* Right: actions + view toggle */}
    <div style={{ display: 'flex', alignItems: 'center', gap: isMobileView ? 6 : 12 }}>
      {!isMobileView && (
        <>
          <button
            onClick={onMapsClick}
            data-testid="admin-header-maps-button"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 13px', borderRadius: 12,
              border: `1px solid ${activeTab === 'maps' ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`,
              cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 13,
              fontWeight: activeTab === 'maps' ? 700 : 600,
              background: activeTab === 'maps' ? ADMIN_PALETTE.ink : ADMIN_PALETTE.surface,
              color: activeTab === 'maps' ? '#FFFFFF' : ADMIN_PALETTE.inkSoft,
            }}
          >
            <Globe2 size={14} /> Maps
          </button>
          <AdminNotificationsBell onSelectTab={onSelectTab} />
          <button
            onClick={onAppViewClick}
            data-testid="admin-exit-to-app-button"
            title="Switch to the guest app view (you stay logged in)"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 13px', borderRadius: 12,
              border: `1px solid ${ADMIN_PALETTE.border}`,
              cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600,
              background: ADMIN_PALETTE.surface, color: ADMIN_PALETTE.inkSoft,
            }}
          >
            <Home size={14} /> App view
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              color: ADMIN_PALETTE.brandRed,
              fontFamily: "'Inria Serif', serif", fontSize: 14,
            }}
          >
            <LogOut size={16} /> Log Out
          </button>
        </>
      )}

      {isMobileView && <AdminNotificationsBell onSelectTab={onSelectTab} />}

      {/* View toggle pill — visible in both modes */}
      <div
        data-testid="admin-view-toggle"
        style={{
          display: 'inline-flex', alignItems: 'center',
          border: `1px solid ${ADMIN_PALETTE.border}`,
          borderRadius: isMobileView ? 999 : 12, overflow: 'hidden',
          background: ADMIN_PALETTE.surfaceMuted,
        }}
      >
        <button
          type="button"
          onClick={() => { if (isMobileView) toggleMobileView(); }}
          data-testid="admin-view-toggle-desktop"
          title="Switch to desktop view"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: isMobileView ? '7px 9px' : '8px 11px', border: 'none',
            background: !isMobileView ? ADMIN_PALETTE.ink : 'transparent',
            color: !isMobileView ? '#FFFFFF' : ADMIN_PALETTE.inkMuted,
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
            fontFamily: "'Manrope', sans-serif",
            transition: 'background 150ms ease, color 150ms ease',
          }}
        >
          <Monitor size={13} />
          {!isMobileView && <span>Desktop</span>}
        </button>
        <button
          type="button"
          onClick={() => { if (!isMobileView) toggleMobileView(); }}
          data-testid="admin-view-toggle-mobile"
          title="Switch to mobile view"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: isMobileView ? '7px 9px' : '8px 11px', border: 'none',
            background: isMobileView ? ADMIN_PALETTE.ink : 'transparent',
            color: isMobileView ? '#FFFFFF' : ADMIN_PALETTE.inkMuted,
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
            fontFamily: "'Manrope', sans-serif",
            transition: 'background 150ms ease, color 150ms ease',
          }}
        >
          <Smartphone size={13} />
          {isMobileView && <span>Mobile</span>}
        </button>
      </div>
    </div>
  </div>
);
